// Platform Admin data + mutations (PRD §5). Staff-only; every call is guarded by
// the platformAdmin custom claim server-side. Tenants + plans are fetched via
// callables (the Admin SDK reads across tenants); mutations go through guarded
// callables.
import { useCallback, useEffect, useState } from 'react';
import { call } from './callable';

export interface PadminTenant {
  id: string;
  name: string;
  planId: string;
  status: string;
  createdAt: number;
  subscribers: number;
  subLimit: number;
  messagesUsed: number;
  msgLimit: number;
}
export interface PadminPlan {
  id: string;
  name: string;
  priceMonthlyUsd: number;
  priceYearlyUsd: number;
  quotas: { subscribers: number; messagesPerMonth: number; aiTokensPerMonth: number; teamSeats: number; teamSeatsYearly?: number; numbers: number };
  /** Free days granted at signup. 0 on paid plans. */
  trialDays?: number;
  isTrial?: boolean;
  /** Quoted price per currency; the rial charged is derived from it. */
  prices?: Record<string, { monthly: number; yearly: number }>;
}

const padminTenantsC = call<any, any>('padminTenants');

export function usePadminTenants() {
  const [tenants, setTenants] = useState<PadminTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    padminTenantsC({ action: 'listTenants' })
      .then((r) => { setTenants(r.data.tenants); setError(null); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);
  return { tenants, loading, error, reload };
}

export function usePadminPlans() {
  const [plans, setPlans] = useState<PadminPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    padminTenantsC({ action: 'listPlans' })
      .then((r) => setPlans(r.data.plans))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { plans, loading, error, reload };
}

export const setTenantStatus = (tenantId: string, suspend: boolean, reason?: string) =>
  padminTenantsC({ action: 'setTenantStatus', tenantId, suspend, reason }).then((r) => r.data);
export const setTenantPlan = (tenantId: string, planId: string) =>
  padminTenantsC({ action: 'setTenantPlan', tenantId, planId }).then((r) => r.data);
export const savePlan = (plan: PadminPlan) =>
  padminTenantsC({ action: 'savePlan', ...plan }).then((r) => r.data);
